/**
 * Client-side mirror of SearchService.Score (C#), so typing re-filters the
 * cached payload instantly and the dropdown never blanks between keystrokes.
 * docs/plans/2026-08-handoff-layout/08-search.md
 *
 *   title starts with query = 3 · title contains = 2 · meta contains = 1 · else 0 (drop)
 */
export function scoreItem(item, term) {
  if (!term) return 1;
  const q = term.toLowerCase();
  const title = (item.title || '').toLowerCase();
  const meta = `${item.searchMeta || ''} ${item.meta || ''}`.toLowerCase();
  if (title.startsWith(q)) return 3;
  if (title.includes(q)) return 2;
  if (meta.includes(q)) return 1;
  return 0;
}

/**
 * Rank one category's rows, keeping at most `max`. Natural order (the order the
 * server returned) breaks ties.
 */
export function rankItems(items = [], term, max = 3) {
  return items
    .map((item, i) => ({ item, score: scoreItem(item, term), i }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .slice(0, max)
    .map((x) => x.item);
}

/**
 * Order the three sections: best item score first, ties broken by match count.
 * Returns [{ key, label, items, total }] with empty sections dropped.
 */
export function rankSections(payload, term, maxPerSection = 3) {
  const defs = [
    { key: 'events', label: 'Events' },
    { key: 'people', label: 'People' },
    { key: 'clubs', label: 'Clubs' },
  ];

  return defs
    .map((def) => {
      const cat = payload?.[def.key] || { items: [], total: 0 };
      const scored = (cat.items || []).map((item) => scoreItem(item, term));
      const matches = scored.filter((s) => s > 0).length;
      const best = scored.length ? Math.max(...scored) : 0;
      return {
        ...def,
        items: rankItems(cat.items, term, maxPerSection),
        // With a query the true count is what actually matched locally; with no
        // query the server's total is authoritative.
        total: term ? Math.max(matches, 0) : (cat.total || 0),
        serverTotal: cat.total || 0,
        best,
        matches,
      };
    })
    .filter((s) => s.items.length > 0)
    .sort((a, b) => b.best - a.best || b.matches - a.matches);
}

export default rankSections;
